import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  Answer,
  AdminDay,
  AdminDeliverableInput,
  AdminDayInput,
  AdminOverview,
  ContentWeek,
  CreateUserInput,
  DayResponse,
  DeliverableView,
  ExerciseCheckResult,
  ExerciseRevealResult,
  ExerciseState,
  ExerciseView,
  MeResponse,
  PlanResponse,
  ProgressView,
  PublicSettings,
  QuizResult,
  ReviewDetail,
  ReviewInput,
  ReviewQueueItem,
  SettingsInput,
  SessionUser,
  StudentDetail,
  StudentRow,
  UpdateUserInput,
  UserRow,
} from "@tomas/shared";
import { api, ApiError, serial } from "./api";

export const keys = {
  me: ["me"] as const,
  plan: ["plan"] as const,
  progress: ["progress"] as const,
  day: (date: string) => ["day", date] as const,
  deliverables: ["deliverables"] as const,
  admin: ["admin"] as const,
  overview: ["admin", "overview"] as const,
  students: ["admin", "students"] as const,
  student: (id: string) => ["admin", "student", id] as const,
  reviews: (status: string) => ["admin", "reviews", status] as const,
  review: (userId: string, deliverableId: string) => ["admin", "review", userId, deliverableId] as const,
  content: ["admin", "content"] as const,
  adminDay: (date: string) => ["admin", "day", date] as const,
  users: ["admin", "users"] as const,
  settings: ["admin", "settings"] as const,
};

// ---------- Sesión ----------

export function useMe() {
  return useQuery({
    queryKey: keys.me,
    queryFn: async () => {
      try {
        return (await api.get<MeResponse | null>("/api/auth/me")) ?? null;
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) return null;
        throw err;
      }
    },
    staleTime: 60_000,
  });
}

export function useLogin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { username: string; password: string }) =>
      api.post<{ user: SessionUser }>("/api/auth/login", input),
    onSuccess: async () => {
      qc.removeQueries({ predicate: (q) => q.queryKey[0] !== "me" });
      await qc.invalidateQueries({ queryKey: keys.me });
    },
  });
}

/**
 * Cierra la sesión y recarga la aplicación en la pantalla de inicio de sesión.
 * La recarga completa garantiza que no quede en memoria ningún dato de la sesión anterior.
 */
export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await qc.cancelQueries();
      await api.post("/api/auth/logout").catch(() => undefined);
    },
    onSettled: () => {
      qc.clear();
      window.location.replace("/login");
    },
  });
}

export function useChangePassword() {
  return useMutation({
    mutationFn: (input: { currentPassword: string; newPassword: string }) => api.post("/api/auth/password", input),
  });
}

// ---------- Estudiante ----------

export const usePlan = () =>
  useQuery({ queryKey: keys.plan, queryFn: () => api.get<PlanResponse>("/api/plan"), staleTime: 5 * 60_000 });

export const useProgress = () => useQuery({ queryKey: keys.progress, queryFn: () => api.get<ProgressView>("/api/progress") });

export const useDay = (date: string) =>
  useQuery({ queryKey: keys.day(date), queryFn: () => api.get<DayResponse>(`/api/days/${date}`), retry: false });

export const useDeliverables = () =>
  useQuery({ queryKey: keys.deliverables, queryFn: () => api.get<DeliverableView[]>("/api/deliverables") });

export function useSaveDayProgress(date: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { tasks?: boolean[]; evidence?: string; challengeDone?: boolean }) =>
      serial(`day:${date}`, () => api.put(`/api/days/${date}/progress`, input)),
    onMutate: async (input) => {
      await qc.cancelQueries({ queryKey: keys.day(date) });
      const previous = qc.getQueryData<DayResponse>(keys.day(date));
      if (previous) {
        qc.setQueryData<DayResponse>(keys.day(date), {
          ...previous,
          progress: { ...previous.progress, ...input },
        });
      }
      return { previous };
    },
    onError: (_err, _input, context) => {
      if (context?.previous) qc.setQueryData(keys.day(date), context.previous);
    },
    onSettled: (_data, _err, input) => {
      if (input.tasks) {
        void qc.invalidateQueries({ queryKey: keys.day(date) });
        void qc.invalidateQueries({ queryKey: keys.progress });
      }
    },
  });
}

export function useSubmitQuiz(date: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { answers: Answer[]; hints: number[] }) =>
      serial(`day:${date}`, () => api.post<QuizResult>(`/api/days/${date}/quiz`, input)),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.day(date) });
      void qc.invalidateQueries({ queryKey: keys.progress });
    },
  });
}

function refreshDeliverable(qc: ReturnType<typeof useQueryClient>, view: DeliverableView) {
  qc.setQueryData<DeliverableView[]>(keys.deliverables, (list) => list?.map((d) => (d.id === view.id ? view : d)));
  qc.setQueryData<DayResponse>(keys.day(view.dueDate), (day) => (day ? { ...day, deliverable: view } : day));
  void qc.invalidateQueries({ queryKey: keys.progress });
}

export function useSaveSubmission(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { criteria?: boolean[]; evidence?: string }) =>
      serial(`deliverable:${id}`, () => api.put<DeliverableView>(`/api/deliverables/${id}`, input)),
    onSuccess: (view) => refreshDeliverable(qc, view),
  });
}

export function useSubmissionAction(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (action: "submit" | "withdraw") =>
      serial(`deliverable:${id}`, () => api.post<DeliverableView>(`/api/deliverables/${id}/${action}`)),
    onSuccess: (view) => {
      refreshDeliverable(qc, view);
      void qc.invalidateQueries({ queryKey: keys.deliverables });
    },
  });
}

// ---------- Ejercicios SQL ----------

/** Dónde está el ejercicio: en el taller de una fecha o en un entregable. */
export type ExerciseScope = { kind: "day"; date: string } | { kind: "deliverable"; id: string; dueDate: string };

/** Actualiza un ejercicio en las vistas en caché (día y entregables) sin esperar a recargar. */
function patchExercise(qc: ReturnType<typeof useQueryClient>, scope: ExerciseScope, id: string, state: ExerciseState, solution: string | null) {
  const patch = (list: ExerciseView[]) => list.map((e) => (e.id === id ? { ...e, state, solution: solution ?? e.solution } : e));
  if (scope.kind === "day") {
    qc.setQueryData<DayResponse>(keys.day(scope.date), (d) => (d ? { ...d, exercises: patch(d.exercises) } : d));
  } else {
    qc.setQueryData<DayResponse>(keys.day(scope.dueDate), (d) =>
      d?.deliverable ? { ...d, deliverable: { ...d.deliverable, exercises: patch(d.deliverable.exercises) } } : d,
    );
    qc.setQueryData<DeliverableView[]>(keys.deliverables, (list) =>
      list?.map((d) => (d.id === scope.id ? { ...d, exercises: patch(d.exercises) } : d)),
    );
  }
}

function refreshAfterExercise(qc: ReturnType<typeof useQueryClient>, scope: ExerciseScope) {
  void qc.invalidateQueries({ queryKey: keys.day(scope.kind === "day" ? scope.date : scope.dueDate) });
  void qc.invalidateQueries({ queryKey: keys.progress });
  if (scope.kind === "deliverable") void qc.invalidateQueries({ queryKey: keys.deliverables });
}

export function useCheckExercise(scope: ExerciseScope, id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (sql: string) => serial(`exercise:${id}`, () => api.post<ExerciseCheckResult>(`/api/exercises/${id}/check`, { sql })),
    onSuccess: (res) => {
      patchExercise(qc, scope, id, res.state, res.solution);
      refreshAfterExercise(qc, scope);
    },
  });
}

export function useRevealExercise(scope: ExerciseScope, id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<ExerciseRevealResult>(`/api/exercises/${id}/reveal`),
    onSuccess: (res) => {
      patchExercise(qc, scope, id, res.state, res.solution);
      refreshAfterExercise(qc, scope);
    },
  });
}

// ---------- Administración ----------

export const useOverview = () =>
  useQuery({ queryKey: keys.overview, queryFn: () => api.get<AdminOverview>("/api/admin/overview"), refetchInterval: 60_000 });

export const useStudents = () =>
  useQuery({ queryKey: keys.students, queryFn: () => api.get<StudentRow[]>("/api/admin/students") });

export const useStudent = (id: string) =>
  useQuery({ queryKey: keys.student(id), queryFn: () => api.get<StudentDetail>(`/api/admin/students/${id}`), retry: false });

export const useReviews = (status: string) =>
  useQuery({
    queryKey: keys.reviews(status),
    queryFn: () => api.get<ReviewQueueItem[]>(`/api/admin/reviews?status=${status}`),
  });

export const useReview = (userId: string, deliverableId: string) =>
  useQuery({
    queryKey: keys.review(userId, deliverableId),
    queryFn: () => api.get<ReviewDetail>(`/api/admin/reviews/${userId}/${deliverableId}`),
    retry: false,
  });

export function useSubmitReview(userId: string, deliverableId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ReviewInput) => api.post<DeliverableView>(`/api/admin/reviews/${userId}/${deliverableId}`, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.admin }),
  });
}

export const useContent = () =>
  useQuery({ queryKey: keys.content, queryFn: () => api.get<ContentWeek[]>("/api/admin/content") });

export const useAdminDay = (date: string) =>
  useQuery({ queryKey: keys.adminDay(date), queryFn: () => api.get<AdminDay>(`/api/admin/days/${date}`), retry: false });

export function useSaveAdminDay(date: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: AdminDayInput) => api.put(`/api/admin/days/${date}`, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.admin });
      void qc.invalidateQueries({ queryKey: keys.plan });
      void qc.invalidateQueries({ queryKey: keys.day(date) });
    },
  });
}

export function useSaveAdminDeliverable(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: AdminDeliverableInput) => api.put(`/api/admin/deliverables/${id}`, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.admin });
      void qc.invalidateQueries({ queryKey: keys.deliverables });
    },
  });
}

export const useUsers = () => useQuery({ queryKey: keys.users, queryFn: () => api.get<UserRow[]>("/api/admin/users") });

export function useCreateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateUserInput) => api.post<UserRow>("/api/admin/users", input),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.admin }),
  });
}

export function useUpdateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...input }: UpdateUserInput & { id: string }) => api.patch<UserRow>(`/api/admin/users/${id}`, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.admin }),
  });
}

export function useResetPassword() {
  return useMutation({
    mutationFn: ({ id, password }: { id: string; password: string }) =>
      api.post(`/api/admin/users/${id}/password`, { password }),
  });
}

export const useSettings = () =>
  useQuery({ queryKey: keys.settings, queryFn: () => api.get<PublicSettings>("/api/admin/settings") });

export function useSaveSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: SettingsInput) => api.put<PublicSettings>("/api/admin/settings", input),
    onSuccess: () => {
      void qc.invalidateQueries();
    },
  });
}
